import { useState } from 'react';
import { X, Heart, Loader } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DonationModal({ isOpen, onClose }) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const presetAmounts = [5, 10, 20, 50];

  const handleDonate = async (donationAmount) => {
    if (!donationAmount || donationAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const origin = window.location.origin;
      const response = await axios.post(`${API}/payments/donation`, null, {
        params: {
          amount: donationAmount,
          origin_url: origin
        }
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to create donation:', error);
      toast.error('Failed to process donation. Please try again.');
      setLoading(false);
    }
  };

  const handlePresetClick = (presetAmount) => {
    handleDonate(presetAmount);
  };

  const handleCustomDonate = () => {
    const customAmount = parseFloat(amount);
    if (isNaN(customAmount) || customAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    handleDonate(customAmount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="cassette-card max-w-md w-full p-8 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-4 right-4 text-[#6B4423] hover:text-[#3D2817] transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <Heart className="w-16 h-16 mx-auto mb-4 text-[#E85D4D]" fill="currentColor" />
          <h2 className="text-3xl retro-title text-[#2C1810] mb-2">Buy Me a Coffee!</h2>
          <p className="text-[#6B4423]">Your support helps me continue providing holistic care</p>
        </div>

        {/* Preset Amount Buttons */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {presetAmounts.map((presetAmount) => (
            <button
              key={presetAmount}
              onClick={() => handlePresetClick(presetAmount)}
              disabled={loading}
              className="retro-button py-4 text-xl font-bold disabled:opacity-50"
            >
              ${presetAmount}
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t-2 border-[#E8934D]"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-[#F4E4C1] text-[#6B4423] font-bold">OR</span>
          </div>
        </div>

        {/* Custom Amount */}
        <div className="mb-6">
          <label className="block text-[#2C1810] font-bold mb-2">Custom Amount</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2C1810] font-bold text-lg">$</span>
              <input
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                disabled={loading}
                className="w-full pl-8 pr-4 py-3 text-lg"
              />
            </div>
            <button
              onClick={handleCustomDonate}
              disabled={loading || !amount}
              className="retro-button px-6 py-3 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                'Donate'
              )}
            </button>
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-xs text-[#6B4423] text-center">
          Secure payment powered by Stripe
        </p>
      </div>
    </div>
  );
}
